import { cloneDeep } from "lodash";
import DataStructure, { sanitize } from "./DataStructure";
import COLORS from "../colors";
import { deepFreeze, timestampToDate } from "../util/misc";
import { router } from "../router";
import { DialogRouter } from "../boot/dialogs";

export const PICKUP_STATUS_FIELDS = {
  type: null,
  pickedUp: {}, // { itemID: quantity }
  completed: null, // timestamp when pickup was completed
  signature: null, // Storage URL of the recorded signature SVG
  signerName: null,
  // Invoice numbers handed out for this event, keyed by sale stateID (see
  // getInvoiceID in functions/invoices.js). Registered so a client status
  // write cannot strip it.
  invoiceClaims: {},
};
deepFreeze(PICKUP_STATUS_FIELDS);

function goToPickup() {
  let item = this._parent.item;
  if (item) {
    router.push({
      name: "pickup",
      params: {
        projectID: item.projectID,
        saleID: item.id,
        pickupID: this._parent.id,
      },
    });
  }
}

function edit() {
  DialogRouter.push({
    name: "event-edit",
    params: {
      eventID: this._parent.id,
    },
  });
}

export const PICKUP_STATUS_TYPES = {
  reschedule: {
    order: 0,
    label: "Reschedule",
    icon: "reschedule",
    color: "negative",
    action: edit,
  },
  ready: {
    order: 1,
    label: "Ready for pickup",
    icon: "loading_ready",
    color: "warning",
    action: goToPickup,
  },
  pickedUpPartial: {
    order: 2,
    label: "Partially picked up",
    icon: "undone",
    color: "warning",
    time: "completed",
    action: goToPickup,
  },
  pickedUp: {
    order: 3,
    label: "Picked up",
    icon: "done",
    color: "positive",
    time: "completed",
    action: goToPickup,
  },
};
deepFreeze(PICKUP_STATUS_TYPES);

export default class PickupStatus extends DataStructure {
  constructor(parent, data = {}) {
    const needsSanitize = Object.keys(PICKUP_STATUS_FIELDS).some(
      (key) => !(key in data)
    );
    if (Object.isFrozen(data) || needsSanitize) {
      data = sanitize(
        Object.isFrozen(data) ? cloneDeep(data) : data,
        PICKUP_STATUS_FIELDS
      );
    }
    data.completed = timestampToDate(data.completed);

    super(data);

    this._parent = parent;
    // Always update type based on current conditions
    this.data.type = this.get();
  }

  get() {
    if (this._parent.data.rescheduleReason) {
      return "reschedule";
    } else if (this.completed) {
      // Pickup complete
      if (this.percentPickedUp.percent < 100) {
        return "pickedUpPartial";
      }
      return "pickedUp";
    } else {
      return "ready";
    }
  }

  get isInProgress() {
    // Pickup doesn't have intermediate states like delivery
    return false;
  }

  // Counts

  get pickedUpCount() {
    return Object.values(this.data.pickedUp).reduce(
      (total, count) => total + count,
      0
    );
  }

  get percentPickedUp() {
    const numerator = this.pickedUpCount;
    const denominator = this._parent.quantity;
    const percent = (100 * numerator) / denominator;
    return { percent, numerator, denominator };
  }

  get itemsPickedUp() {
    return { ...this.data.pickedUp };
  }

  // Formatting

  get time() {
    const type = PICKUP_STATUS_TYPES[this.get()];
    return type?.time ? this.data[type.time] : null;
  }

  get type() {
    const type = PICKUP_STATUS_TYPES[this.get()];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get action() {
    let type = this.type;
    return type && type.action ? type.action.bind(this) : null;
  }
}
